/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import cx from 'classnames';
import { FormikProps } from 'formik';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import CheckIcon from '@mui/icons-material/Check';
import ErrorIcon from '@mui/icons-material/Error';
import { Button, CircularProgress, Stack, Tooltip, Zoom } from '@mui/material';

import { ClusterStates, ServiceTypeMatrix } from 'shared';

import { AlignCenter } from '../../../../components/AlignCenter/AlignCenter';
import { HUModal2 } from '../../../../components/HUModal2/HUModal2';
import { SelectItemCatalogue } from '../../../../components/SelectItemCatalogue/SelectItemCatalogue';
import { ServiceIcon } from '../../../../components/ServiceIcon';
import { ArrowRoundedIcon } from '../../../../components/svg-icons/ArrowRoundedIcon';
import { Namespaces } from '../../../../i18n';
import { useSelectEntityModalStyles } from '../../../../styles/selectEntityModal';
import { ClusterConfiguration } from '../../../../types/cluster/ClusterConfiguration';
import {
  fetchCluster,
  fetchClusterDetails,
  fetchClusterStatus,
  fetchContinuousBackupOldestRestorePoint as fetchRestorePoint,
  fetchUserServices,
} from '../../../../utils/queryFns';
import { useObjectState } from '../../../../utils/useObjectState';
import {
  CreateClusterFromBackupValues,
  OrganizationVO,
  ProjectVO,
  SearchOption,
  ServiceVO,
} from '../../CreateClusterOverlay.types';
import { SearchAutocomplete } from '../SearchAutocomplete/SearchAutocomplete';

type SelectOrganizationClusterProps = FormikProps<CreateClusterFromBackupValues> & {
  titleOverride?: string;
  purpose: 'simple' | 'cluster_creation_from_backup';
};

type State = {
  isOpen: boolean;
  wasOpen: boolean;
  organization?: OrganizationVO;
  project?: ProjectVO;
  service?: ServiceVO;
};

export function SelectCluster(props: SelectOrganizationClusterProps) {
  const { purpose, ...formProps } = props;

  const { values, setFieldValue } = formProps;
  const semClasses = useSelectEntityModalStyles();
  const { t } = useTranslation([Namespaces.CreateCluster, Namespaces.Common]);
  const [state, setState] = useObjectState<State>({
    isOpen: false,
    wasOpen: false,
    organization: undefined,
    project: undefined,
    service: undefined,
  });

  const { service, isOpen, wasOpen, project, organization } = state;

  const servicesQuery = useQuery([fetchUserServices.key], fetchUserServices.fn);

  const organizations = useMemo(() => servicesQuery.data?.data.payload.organizations ?? [], [servicesQuery]);
  const projects = useMemo(() => organization?.projects ?? [], [organization]);
  const services = useMemo(() => project?.services ?? [], [project]);

  const setOrganization = useCallback(
    (organization?: OrganizationVO) => setState({ organization, project: undefined, service: undefined }),
    [setState],
  );
  const setProject = useCallback((project?: ProjectVO) => setState({ project, service: undefined }), [setState]);
  const setService = useCallback((service?: ServiceVO) => setState({ service }), [setState]);
  const onCancel = useCallback(() => {
    setState({ isOpen: false, wasOpen: true });
    setTimeout(() => setState({ organization: undefined, project: undefined, service: undefined }), 200);
  }, [setState]);

  const ids = useMemo(
    () => ({ organizationId: organization?.id, projectId: project?.id, clusterId: service?.id }),
    [organization, project, service],
  );
  const queryOptions = { enabled: !!service, retry: false, cache: 0 } as const;
  const restorePointQuery = useQuery([fetchRestorePoint.key, ids], fetchRestorePoint.fn, queryOptions);
  const serviceQuery = useQuery([fetchCluster.key, ids], fetchCluster.fn, queryOptions);
  const serviceDetailsQuery = useQuery([fetchClusterDetails.key, ids], fetchClusterDetails.fn, queryOptions);
  const serviceStatusQuery = useQuery([fetchClusterStatus.key, ids], fetchClusterStatus.fn, queryOptions);

  const eligibilityCheck = useMemo<
    | { state: 'pending' }
    | { state: 'loading' }
    | {
        state: 'pass';
        restorePoint?: string;
        latestConfiguration: ClusterConfiguration;
        backupConfiguration: Record<
          'logical' | 'continuous',
          { enabled: boolean; schedule: string; daysToRetain: number }
        >;
      }
    | { state: 'fail'; reason: string }
  >(() => {
    if (!(organization && project && service)) {
      return { state: 'pending' };
    }

    if (
      restorePointQuery.isLoading ||
      serviceQuery.isLoading ||
      serviceDetailsQuery.isLoading ||
      serviceStatusQuery.isLoading
    ) {
      return { state: 'loading' };
    }

    // Restore point query left out on purpose
    if (serviceQuery.isLoading || serviceDetailsQuery.isError || serviceStatusQuery.isError) {
      return { state: 'fail', reason: t('cluster:create.selector.tooltips.failed_to_get_service_state') };
    }

    const restorePoint = restorePointQuery.data?.payload.lastModified;
    if (purpose === 'cluster_creation_from_backup') {
      if (serviceStatusQuery.data?.payload.status === ClusterStates.Creating) {
        return { state: 'fail', reason: t('cluster:create.selector.tooltips.service_is_creating') };
      }

      if (!serviceDetailsQuery.data?.payload.backupConfiguration.continuous.enabled) {
        return { state: 'fail', reason: t('cluster:create.selector.tooltips.continuous_backups_not_enabled') };
      }

      if (!restorePoint) {
        return { state: 'fail', reason: t('cluster:create.selector.tooltips.no_oldest_restore_point') };
      }
    }

    return {
      state: 'pass',
      restorePoint,
      latestConfiguration: serviceQuery.data!.payload.configuration.find(c => c.endedAt === 'infinity')!,
      backupConfiguration: serviceDetailsQuery.data!.payload.backupConfiguration,
    };
  }, [
    organization,
    project,
    service,
    restorePointQuery.isLoading,
    restorePointQuery.data?.payload.lastModified,
    serviceQuery.isLoading,
    serviceQuery.data,
    serviceDetailsQuery.isLoading,
    serviceDetailsQuery.isError,
    serviceDetailsQuery.data,
    serviceStatusQuery.isLoading,
    serviceStatusQuery.isError,
    serviceStatusQuery.data?.payload.status,
    purpose,
    t,
  ]);

  const onSubmit = useCallback(async () => {
    if (service && project && organization && eligibilityCheck.state === 'pass') {
      const backup = eligibilityCheck.backupConfiguration;
      // wtf
      void setFieldValue('allowedOrganizations', [organization.id]);
      void setFieldValue('backupCluster', service);
      void setFieldValue('backupClusterConfiguration', eligibilityCheck.latestConfiguration);
      void setFieldValue('backupClusterId', service.id);
      void setFieldValue('backupConfigurationContinuousDaysToRetain', backup.continuous.daysToRetain);
      void setFieldValue('backupConfigurationContinuousEnabled', backup.continuous.enabled);
      void setFieldValue('backupConfigurationContinuousSchedule', backup.continuous.schedule);
      void setFieldValue('backupConfigurationLogicalDaysToRetain', backup.logical.daysToRetain);
      void setFieldValue('backupConfigurationLogicalEnabled', backup.logical.enabled);
      void setFieldValue('backupConfigurationLogicalSchedule', backup.logical.schedule);
      void setFieldValue('backupOrganizationId', organization.id);
      void setFieldValue('backupProjectId', project.id);
      void setFieldValue('oldestRestorePoint', eligibilityCheck.restorePoint);
      void setFieldValue('organizationId', organization.id);
      void setFieldValue('sourceClusterBackupConfiguration', backup);
      setState({ isOpen: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eligibilityCheck.state, organization, project, service, setFieldValue, setState]);

  // Pre-select when the component is opened
  useEffect(() => {
    if (!organization && values.backupOrganizationId) {
      setOrganization(organizations.find(o => o.id === values.backupOrganizationId));
    }
    if (!project && values.backupProjectId) {
      setProject(projects.find(p => p.id === values.backupProjectId));
    }
    if (!service && values.backupClusterId) {
      setService(services.find(s => s.id === values.backupClusterId));
      if (!wasOpen && !values.backupCluster) {
        setState({ isOpen: true });
      }
    }
  }, [
    servicesQuery.data,
    organizations,
    projects,
    services,
    organization,
    values.backupOrganizationId,
    values.backupProjectId,
    values.backupClusterId,
    values.backupCluster,
    project,
    service,
    setOrganization,
    setProject,
    setService,
    wasOpen,
    setState,
  ]);

  const searchOptions: SearchOption[] = useMemo(() => {
    const options: SearchOption[] = [];
    organizations.forEach(o => {
      options.push({ group: 'organizations', entity: { organization: o } });
      o.projects.forEach(p => {
        options.push({ group: 'projects', entity: { project: p, organization: o } });
        p.services.forEach(s =>
          options.push({ group: 'services', entity: { organization: o, project: p, service: s } }),
        );
      });
    });
    return options;
  }, [organizations]);

  const onSearchDropdownSelect = useCallback(
    (props: { organization?: OrganizationVO; project?: ProjectVO; service?: ServiceVO }) => {
      setOrganization(props.organization);
      setProject(props.project);
      setService(props.service);
    },
    [setOrganization, setProject, setService],
  );

  return (
    <>
      <div className={semClasses.root}>
        <Button
          size="large"
          variant={values.backupCluster ? 'contained' : 'outlined'}
          onClick={() => setState({ isOpen: true })}
          className={semClasses.rootButton}
          disabled={servicesQuery.isLoading}
        >
          {values.backupCluster?.name || t('labels.select_cluster')}
          {servicesQuery.isLoading ? (
            <CircularProgress color="primary" size={20} disableShrink />
          ) : (
            <ArrowRoundedIcon className={semClasses.rootButtonIcon} />
          )}
        </Button>
      </div>

      <HUModal2
        open={isOpen}
        size="large"
        title={props.titleOverride ?? t('labels.select_backup_cluster_modal_title')}
        paperClassName={semClasses.modalPaper}
        onClose={onCancel}
        footer={({ footerClass }) => (
          <div className={footerClass}>
            <Tooltip
              open
              arrow
              placement="top"
              title={eligibilityCheck.state === 'fail' ? eligibilityCheck.reason : ''}
            >
              <Button
                data-cy="select-cluster-submit-button"
                color="primary"
                variant="contained"
                fullWidth
                disabled={eligibilityCheck.state !== 'pass'}
                onClick={onSubmit}
                startIcon={
                  eligibilityCheck.state === 'loading' ? (
                    <CircularProgress color="primary" size={24} className={semClasses.loaderMl} disableShrink />
                  ) : (
                    <Zoom in>
                      {eligibilityCheck.state === 'pass' ? (
                        <CheckIcon />
                      ) : eligibilityCheck.state === 'fail' ? (
                        <ErrorIcon />
                      ) : (
                        <div />
                      )}
                    </Zoom>
                  )
                }
              >
                {t(`cluster:create.selector.submit_button.${eligibilityCheck.state}`)}
              </Button>
            </Tooltip>
            <Button data-cy="select-cluster-cancel-button" color="primary" variant="text" fullWidth onClick={onCancel}>
              {t('labels.cancel')}
            </Button>
          </div>
        )}
      >
        <div className={semClasses.body}>
          <SearchAutocomplete purpose={purpose} searchOptions={searchOptions} onSelect={onSearchDropdownSelect} />
          <div className={cx(semClasses.row, semClasses.row3Columns)}>
            <div className={semClasses.column}>
              {servicesQuery.isLoading ? (
                <AlignCenter>
                  <CircularProgress color="primary" size={40} />
                </AlignCenter>
              ) : (
                <SelectItemCatalogue
                  options={organizations}
                  selected={organization}
                  getOptionLabel={o => o.name}
                  getOptionSelected={(o, s) => o.id === s?.id}
                  onItemClick={(e, v) => setOrganization(v)}
                  title={t('labels.organizations')}
                  noOptionsText={t('labels.org_does_not_have_suitable_clusters')}
                  buttonProps={{ disableRipple: true }}
                />
              )}
            </div>
            <div className={semClasses.column}>
              {servicesQuery.isLoading ? (
                <AlignCenter>
                  <CircularProgress color="primary" size={40} />
                </AlignCenter>
              ) : !organization ? (
                <AlignCenter>
                  <div className={semClasses.tipText}>{t('labels.select_organization_from_left')}</div>
                </AlignCenter>
              ) : !organization.projects.length ? (
                <AlignCenter>
                  <div className={semClasses.tipText}>{t('labels.org_does_not_have_projects')}</div>
                </AlignCenter>
              ) : (
                <SelectItemCatalogue
                  options={projects}
                  selected={project}
                  getOptionLabel={o => o.name}
                  getOptionSelected={(o, s) => o.id === s?.id}
                  onItemClick={(e, v) => setProject(v)}
                  title={t('labels.projects')}
                  noOptionsText={t('labels.org_does_not_have_projects')}
                  buttonProps={{ disableRipple: true }}
                />
              )}
            </div>
            <div className={semClasses.column}>
              {servicesQuery.isLoading ? (
                <AlignCenter>
                  <CircularProgress color="primary" size={40} />
                </AlignCenter>
              ) : !project ? (
                <AlignCenter>
                  <div className={semClasses.tipText}>{t('labels.select_project_from_left')}</div>
                </AlignCenter>
              ) : !project.services.length ? (
                <AlignCenter>
                  <div className={semClasses.tipText}>{t('labels.project_does_not_have_clusters')}</div>
                </AlignCenter>
              ) : (
                <SelectItemCatalogue
                  options={services}
                  selected={service}
                  getOptionLabel={o => o.name}
                  getOptionSelected={(o, s) => o.id === s?.id}
                  onItemClick={(e, v) => setService(v)}
                  title={t('labels.services')}
                  iconType="check"
                  noOptionsText={t('labels.project_does_not_have_clusters')}
                  renderOption={({ key, isActive, option, buttonProps, optionLabel, onClick }) => {
                    const disabled =
                      purpose === 'cluster_creation_from_backup' &&
                      !ServiceTypeMatrix.sourceClusterForCreatingPostgresqlFromBackup[option.type];
                    const disabledDueTo = disabled ? t('cluster:create.selector.tooltips.unsuitable_service_type') : '';

                    return (
                      <Tooltip key={key} title={disabledDueTo}>
                        <SelectItemCatalogue.Button
                          {...buttonProps}
                          data-cy={`select-item-catalogue-${optionLabel}`}
                          onClick={onClick}
                          disabled={disabled}
                          isActive={isActive && !disabled}
                          component="div"
                          autoFocus={!isActive && key === 0}
                        >
                          <Stack direction="row" gap={1}>
                            <ServiceIcon serviceType={option.type} variant="minimal" />
                            {optionLabel}
                          </Stack>
                        </SelectItemCatalogue.Button>
                      </Tooltip>
                    );
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </HUModal2>
    </>
  );
}
